import Decimal from 'decimal.js/decimal'
import moment, { unitOfTime } from 'moment'

import { dateConstants } from '@/modules/base/util/constants'
import { ShoppingCartItems } from '@/modules/moduleSubscription/types/ShoppingCart'
import { ModuleWithCount, SubscriptionWithCount } from '@/modules/moduleSubscription/types/module'
import calculatePrice from '@/modules/moduleSubscription/components/_helpers/calculatePrice'
import getDiscountSplit from '@/modules/moduleSubscription/components/_helpers/getDiscountSplit'
import checkDiscount from '@/modules/base/components/modal/upgrade-modal/subscription-selection/_helpers/checkDiscount'
import { getDiscountMonths } from '@/modules/base/components/modal/upgrade-modal-v2/_helpers/getPlanPrice'
import { getAddonTitle } from '@/modules/base/components/modal/upgrade-modal-v2/_helpers/addonDisplay'

export interface SelectionPriceRow {
  id: string
  pricePerMonth: number
  months: number
}

export interface SelectionLine {
  id: string
  type: string
  name: string
  title: string
  count: number
  isPlan: boolean
  // price rows as listed below the title, e.g. "59 € × 12 Monate" - a partial discount yields two rows
  rows: SelectionPriceRow[]
  total: number
  // total without the plan discount, only set when a discount applies
  regularTotal?: number
  discountPercent?: number
  discountMonths?: number
}

type CartItem = ModuleWithCount | SubscriptionWithCount

const getMonthDivider = (duration: number, period: string): number => {
  if (!duration) return 1
  return Math.round(moment().add(duration, period as unitOfTime.DurationConstructor).diff(moment(), 'months', true)) || 1
}

const getBilledMonths = (period: string) => period === dateConstants.MONTH ? 1 : 12

const getLine = (type: string, item: CartItem, period: string): SelectionLine | null => {
  const priceValues = item.prices?.find(price => price.period === period)?.values?.[0]
  if (!priceValues) return null

  const isPlan = Boolean(item.type)
  const count = item.count || 1
  const price = calculatePrice(priceValues, item.type, period)
  if (price === null) return null

  const monthDivider = getMonthDivider(priceValues.duration, period)
  const billedMonths = getBilledMonths(period)
  const total = new Decimal(price).times(count).toDP(2).toNumber()

  const line: SelectionLine = {
    id: `${type}-${item.name}`,
    type,
    name: item.name,
    // add-ons carry the names of the design ("Abo-Rechnungen"), plans their own title
    title: isPlan ? item.title : getAddonTitle(item.name, item.title),
    count,
    isPlan,
    rows: [],
    total,
  }

  const discountSplit = isPlan ? getDiscountSplit(priceValues, period, item.type) : null
  if (discountSplit) {
    line.rows = [
      { id: 'discounted', pricePerMonth: discountSplit.discountedPricePerMonth, months: discountSplit.discountedMonths },
      { id: 'regular', pricePerMonth: discountSplit.regularPricePerMonth, months: discountSplit.regularMonths },
    ]
  } else {
    line.rows = [{
      id: 'single',
      pricePerMonth: new Decimal(price).dividedBy(monthDivider).toDP(2).toNumber(),
      months: billedMonths,
    }]
  }

  const { reducedPrice } = priceValues
  if (isPlan && reducedPrice?.percent && checkDiscount(item.type, reducedPrice)) {
    line.discountPercent = reducedPrice.percent
    // the duration of the promotion itself - the split only exists for part of a billed year, a
    // monthly billing or a promotion of a year or longer would lose its duration otherwise
    line.discountMonths = getDiscountMonths(reducedPrice)
    line.regularTotal = new Decimal(priceValues.pricePerDuration).times(count).toDP(2).toNumber()
  }

  return line
}

/**
 * Flattens the shopping cart into the lines shown in "Deine Auswahl" and on the success page:
 * plans first, add-ons afterwards, each with the price rows of the billed period.
 */
const getSelectionLines = (items: ShoppingCartItems, period: string): SelectionLine[] => {
  const lines = Object.keys(items || {}).flatMap(type => {
    return Object.values(items[type] || {}).map(item => getLine(type, item, period))
  }).filter(Boolean)

  return [...lines.filter(line => line.isPlan), ...lines.filter(line => !line.isPlan)]
}

export default getSelectionLines
